<script setup lang="ts">
import { authApi } from "@/api/authApi";
import { AuthSectionEnum } from "@/types/enums";
import GoogleIcon from "@/ui/components/icons/GoogleIcon.vue";
import { handleError } from "@/utils/errorHelper";
import useVuelidate from "@vuelidate/core";
import { email, required } from "@vuelidate/validators";
import { computed, ref } from "vue";

const loading = ref(false);
const socialActivated = ref(false);
const error = ref("");
const emailAddress = ref("");

const rules = computed(() => ({
  emailAddress: { required, email },
}));
const v$ = useVuelidate(rules, { emailAddress });

const emailErrors = computed(() => {
  const emailValidator = v$.value.emailAddress as any;
  if (emailValidator.$dirty && emailValidator.$invalid) {
    if (emailValidator.required?.$invalid) return "Email is required.";
    if (emailValidator.email?.$invalid) return "Please enter a valid email address.";
  }
  return null;
});

const submit = async () => {
  const emailValid = await v$.value.$validate();
  if (!emailValid) return;

  loading.value = true;
  try {
    const result = await authApi.checkEmail({ email: emailAddress.value });
    emit("update:emailExists", result.data.exists);
    emit("update:header", result.data.exists ? "Sign in to your account" : "Create an account");
    emit("update:emailAddress", emailAddress.value);
    emit("update:activeSection", AuthSectionEnum.Password);
  } catch (responseError) {
    error.value = handleError(responseError);
  } finally {
    loading.value = false;
  }
};

async function getSocialRedirectUrl(provider: string) {
  socialActivated.value = true;
  loading.value = true;
  try {
    const redirect = await authApi.getSocialRedirect(provider);

    if (redirect) {
      window.location.href = redirect;
    }
  } catch (responseError: unknown) {
    error.value = handleError(responseError);
    socialActivated.value = false;
    loading.value = false;
  }
}

const emit = defineEmits<{
  (e: "update:activeSection", value: AuthSectionEnum): void;
  (e: "update:emailExists", value: boolean): void;
  (e: "update:emailAddress", value: string): void;
  (e: "update:header", value: string): void;
}>();
</script>

<template>
  <form @submit.prevent="submit">
    <Button
      @click="getSocialRedirectUrl('google')"
      type="button"
      :disabled="loading"
      class="w-full border-black bg-black hover:bg-black/90"
    >
      <GoogleIcon class="h-5 w-5" />
      <span class="text-[15px] font-medium text-white">Continue with Google</span>

      <i v-if="loading && socialActivated" class="pi pi-spin pi-spinner text-white"></i>
    </Button>
    <Divider layout="horizontal" class="!flex md:!hidden">
      <span class="text-[14px] font-medium tracking-wider text-slate-500">OR</span>
    </Divider>
    <label>Enter your email address</label>
    <InputText
      v-model="emailAddress"
      placeholder="Enter your email"
      @blur="v$.emailAddress.$touch()"
      :class="{ 'p-invalid': v$.emailAddress.$invalid && v$.emailAddress.$dirty }"
      type="email"
      :disabled="loading"
      aria-label="Email address input"
    />
    <div v-if="emailErrors || error" class="p-error">
      {{ emailErrors ?? error }}
    </div>
    <Button
      type="submit"
      :disabled="loading"
      class="mt-4 w-full !px-8"
      aria-label="Continue to the next step"
    >
      <i v-if="loading && !socialActivated" class="pi pi-spin pi-spinner"></i>
      <span class="text-[15px] font-medium text-black">Continue</span>
    </Button>
  </form>
</template>
